import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowUpRight, BookOpen, Bot, Check, CircleAlert, Copy, GitBranch, Hand, LayoutGrid, Link2, MessagesSquare, MoreHorizontal, Pause, Play, Plus,
  RefreshCw, Sparkles, Star,
} from 'lucide-react';
import { WsSegmented } from 'src/stage/WsControls';
import { AgentAvatar, CharacterStage, PageHeader, Skeleton, StagePage, Status, WsDialog } from 'src/stage/StageUi';
import { characterFor, characterForId } from 'src/stage/characters';
import { currentConnection } from 'src/features/Broadcasts/broadcastApi';
import { AuthContext } from 'src/context/AuthContext';
import useAiConnection, { aiBotUrl, aiGuidedUrl, aiLibraryUrl, aiStatus, aiTenant, aiUrl } from 'src/features/AiChannel/useAiConnection';
import './bots.css';

// AUTO-WA-39 Bots: the bots from Auto Appy AI that answer this number's chats.
// Bots are built and tested in AI; here you choose which ones answer, the
// default, and pause everything. Keys and test mode live in Settings.
export const botKind = (bot) => (bot.adapter === 'static_flow' ? 'guided' : 'ai');
const KINDS = { ai: { label: 'AI chat', icon: Sparkles }, guided: { label: 'Guided chat', icon: GitBranch } };

function BotMenu({ bot, onCopy, isDefault, onDefault, disabled, tenant }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <span ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" className="ws-row-more" onClick={() => setOpen((value) => !value)} aria-haspopup="menu" aria-expanded={open} aria-label={`More actions for ${bot.name}`}>
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="ws-row-menu" role="menu" aria-label={`Actions for ${bot.name}`}>
          <a role="menuitem" className="ws-menu-item" href={aiBotUrl(tenant, bot)} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}><ArrowUpRight size={15} />Edit in Auto Appy AI</a>
          {!isDefault && <button role="menuitem" type="button" className="ws-menu-item" disabled={disabled} onClick={() => { setOpen(false); onDefault(bot); }}><Star size={15} />Make default</button>}
          <button role="menuitem" type="button" className="ws-menu-item" onClick={() => { setOpen(false); onCopy(bot); }}><Copy size={15} />Copy bot ID</button>
        </div>
      )}
    </span>
  );
}

function BotCard({ bot, view, tenant, isDefault, busy, onDefault, onCopy }) {
  const kind = KINDS[botKind(bot)];
  const character = characterForId(bot.id);
  const KindIcon = kind.icon;
  return (
    <article className={`ws-card wa-bot${isDefault ? ' is-default' : ''}`} aria-labelledby={`bot-${bot.id}`}>
      <div className="wa-bot-top">
        <AgentAvatar characterKey={character.key} size={48} name={bot.name} />
        <div className="ws-grow" style={{ minWidth: 0 }}>
          <h3 className="ws-h3 wa-bot-name" id={`bot-${bot.id}`}>{bot.name}</h3>
          <div className="wa-bot-meta">
            <span className={`wa-kind wa-kind--${botKind(bot)}`}><KindIcon size={12} aria-hidden="true" />{kind.label}</span>
            <span className="ws-muted">v{bot.version}</span>
          </div>
        </div>
        <BotMenu bot={bot} tenant={tenant} isDefault={isDefault} disabled={busy || !bot.active || Boolean(view.error)} onDefault={onDefault} onCopy={onCopy} />
      </div>
      <p className="wa-bot-desc">{bot.description || 'No description in Auto Appy AI yet.'}</p>
      {(bot.capabilities || []).length > 0 && (
        <div className="wa-bot-caps">{bot.capabilities.slice(0, 4).map((capability) => <span key={capability} className="ws-chip">{capability.replaceAll('_', ' ')}</span>)}</div>
      )}
      <div className="wa-bot-foot">
        {bot.active ? <Status tone="live">Published</Status> : <Status tone="paused">Paused in AI</Status>}
        {isDefault
          ? <span className="wa-default"><Check size={13} strokeWidth={2.6} aria-hidden="true" />Answers new chats</span>
          : <button type="button" className="ws-agent-open" disabled={busy || !bot.active || Boolean(view.error)} onClick={() => onDefault(bot)}>Make default</button>}
      </div>
    </article>
  );
}

function ConnectDialog({ view, tenant, onClose, onSave, initialBotId }) {
  const first = !view.data;
  const [botId, setBotId] = useState(initialBotId || '');
  const [key, setKey] = useState('');
  const [makeDefault, setMakeDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    const id = botId.trim();
    if (!id || (first && !key)) return;
    setBusy(true); setError('');
    try {
      await onSave({
        channel_id: view.data?.channel_id || null,
        bot_id: id,
        ...(key ? { api_key: key } : {}),
        enabled: view.data ? Boolean(view.data.enabled) : true,
        make_default: first ? true : makeDefault,
        expected_version: view.channel?.version || null,
      });
      onClose(true);
    } catch (failure) {
      setError(failure.message);
      setBusy(false);
    }
  };
  return (
    <WsDialog title={first ? 'Connect your first bot' : 'Connect another bot'} description="Publish and test the bot in Auto Appy AI first, then paste its bot ID here." onClose={() => !busy && onClose(false)} wide>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="ws-field">
          <label htmlFor="bot-id">Published bot ID</label>
          <input id="bot-id" className="ws-input" value={botId} onChange={(event) => setBotId(event.target.value)} placeholder="Paste the bot ID from Auto Appy AI" required disabled={busy} autoComplete="off" />
          <span className="wa-help">Use the published bot ID, not a training library ID. <a href={aiUrl(tenant, { tab: 'bots' })} target="_blank" rel="noreferrer" style={{ color: '#0F6392', fontWeight: 550 }}>Find it in AI ↗</a></span>
        </div>
        {first && (
          <div className="ws-field">
            <label htmlFor="ai-key">Auto Appy AI API key</label>
            <input id="ai-key" className="ws-input" type="password" autoComplete="new-password" value={key} onChange={(event) => setKey(event.target.value)} placeholder="A read/write AI key for this organisation" required disabled={busy} />
            <span className="wa-help">Saved encrypted and never shown again. Replace it later in Settings → AI connection.</span>
          </div>
        )}
        {!first && (
          <label className="wa-switch">
            <input type="checkbox" checked={makeDefault} onChange={(event) => setMakeDefault(event.target.checked)} disabled={busy} />
            <span className="wa-switch-track" aria-hidden="true" />Answer new chats with this bot
          </label>
        )}
        {error && <div className="wa-notice wa-notice--bad" role="alert"><CircleAlert size={15} /><span>{error}</span></div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => onClose(false)} disabled={busy}>Cancel</button>
          <button type="submit" className="ws-btn ws-btn--primary" disabled={busy || !botId.trim() || (first && !key)}><Link2 size={15} />{busy ? 'Connecting…' : 'Connect bot'}</button>
        </div>
      </form>
    </WsDialog>
  );
}

function StatusHero({ view, status, defaultBot, busy, onToggle, loading }) {
  const character = characterFor(defaultBot ? characterForId(defaultBot.id).key : 'appy');
  const on = status.key === 'on' || status.key === 'test';
  return (
    <section className="ws-spot wa-bots-hero ws-rise" aria-label="AI replies">
      <div className="wa-bots-hero-copy">
        <span className="ws-eyebrow">{on ? 'Answering now' : 'AI replies'}</span>
        {loading ? <Skeleton width={260} height={28} style={{ background: 'rgba(255,255,255,.14)' }} /> : (
          <h2 className="wa-bots-hero-title">
            {!view.data && 'No bot answers yet'}
            {view.data && on && (defaultBot ? <>{defaultBot.name} answers new chats</> : 'AI answers new chats')}
            {view.data && !on && status.label}
          </h2>
        )}
        <p className="ws-muted">
          {!view.data && 'Connect a bot from Auto Appy AI and it replies to new WhatsApp messages. Your team can take over any chat.'}
          {view.data && status.key === 'test' && `Test mode: only ${view.data.test_recipient} gets AI replies. Everyone else waits for your team.`}
          {view.data && status.key === 'on' && 'New messages go to the default bot. Anyone on your team can take over a chat from Chats.'}
          {view.data && status.key === 'paused' && 'New messages stay in Chats for your team. Nothing is lost while paused.'}
          {view.data && status.key === 'channel' && 'This channel is paused in Auto Appy AI. Turn it back on there.'}
          {view.data && status.key === 'unknown' && view.error}
        </p>
        <div className="wa-bots-hero-actions">
          {view.data && (status.key === 'paused'
            ? <button type="button" className="ws-btn ws-btn--white" onClick={() => onToggle(true)} disabled={busy}><Play size={15} />Turn AI replies on</button>
            : <button type="button" className="ws-btn ws-btn--glass" onClick={() => onToggle(false)} disabled={busy}><Pause size={15} />Pause AI replies</button>)}
          <Link className="ws-btn ws-btn--glass" to="/conversation"><MessagesSquare size={15} />Open Chats</Link>
          {view.data && <Link className="ws-btn ws-btn--glass" to="/settings/ai">Key and test mode</Link>}
        </div>
      </div>
      <div className="wa-bots-hero-art" aria-hidden="true"><CharacterStage character={character} height={200} /></div>
    </section>
  );
}

function GuidedExplainer({ tenant }) {
  const steps = [
    'In Auto Appy AI, build a guided chat: text, images, PDFs, reply buttons or list menus at each step.',
    'Connect each option to its next step. Branches can loop back or join another branch.',
    'Publish and test it, then connect its bot ID here. It then answers like any other bot.',
  ];
  return (
    <section className="ws-card ws-card-pad wa-guided" aria-label="About guided chats">
      <div className="wa-guided-head">
        <span className="ws-icon-tile" aria-hidden="true"><GitBranch size={18} /></span>
        <div className="ws-grow">
          <h2 className="ws-h3">Guided chats: fixed menus and steps</h2>
          <p className="ws-muted">Every reply is one you approved. Use them for order status, store timings or collecting details.</p>
        </div>
        <a className="ws-btn ws-btn--quiet ws-btn--sm" href={aiGuidedUrl(tenant)} target="_blank" rel="noreferrer">Build in AI<ArrowUpRight size={14} /></a>
      </div>
      <ol className="wa-guided-steps">{steps.map((step, index) => <li key={step}><span className="wa-step-num">{index + 1}</span>{step}</li>)}</ol>
      <p className="wa-help">Invalid answers restart the flow, and old buttons can’t jump steps. Dates collected by a guided chat are preferences, not confirmed bookings.</p>
    </section>
  );
}

export default function Bots() {
  const [params, setParams] = useSearchParams();
  const connection = currentConnection();
  const { currentUser } = useContext(AuthContext) || {};
  const { view, loading, error, canManage, reload, save } = useAiConnection(connection?.workspaceId);
  const tenant = aiTenant(view, currentUser);
  const [kind, setKind] = useState(params.get('kind') === 'guided' ? 'guided' : 'all');
  const [connecting, setConnecting] = useState(Boolean(params.get('bot')));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const status = aiStatus(view);
  const defaultBot = view.bots.find((bot) => bot.id === view.channel?.default_bot_id) || null;
  const counts = useMemo(() => ({ all: view.bots.length, ai: view.bots.filter((bot) => botKind(bot) === 'ai').length, guided: view.bots.filter((bot) => botKind(bot) === 'guided').length }), [view.bots]);
  const shown = view.bots.filter((bot) => kind === 'all' || botKind(bot) === kind)
    .sort((a, b) => (b.id === view.channel?.default_bot_id) - (a.id === view.channel?.default_bot_id) || String(a.name).localeCompare(String(b.name)));
  const filters = [
    { id: 'all', label: 'All', icon: LayoutGrid, count: counts.all },
    { id: 'ai', label: 'AI chat', icon: Sparkles, count: counts.ai },
    { id: 'guided', label: 'Guided chat', icon: GitBranch, count: counts.guided },
  ];
  const changeKind = (next) => { setKind(next); const copy = new URLSearchParams(params); if (next === 'all') copy.delete('kind'); else copy.set('kind', next); setParams(copy, { replace: true }); };

  const run = async (payload, message) => {
    setBusy(true); setNotice(null);
    try { await save(payload); setNotice({ tone: 'good', text: message }); }
    catch (failure) { setNotice({ tone: 'bad', text: failure.message }); }
    finally { setBusy(false); }
  };
  const makeDefault = (bot) => run({ channel_id: view.data.channel_id, bot_id: bot.id, enabled: view.data.enabled, make_default: true, expected_version: view.channel?.version }, `${bot.name} now answers new chats. Chats already with a bot keep it.`);
  const toggle = (enabled) => run({ channel_id: view.data.channel_id, enabled }, enabled ? 'AI replies are on.' : 'AI replies paused. New messages stay in Chats for your team.');
  const copy = async (bot) => {
    try { await navigator.clipboard.writeText(bot.id); setNotice({ tone: 'good', text: `Copied the bot ID for ${bot.name}.` }); }
    catch { setNotice({ tone: 'info', text: `Bot ID: ${bot.id}` }); }
  };
  const closeConnect = (connected) => {
    setConnecting(false);
    if (params.get('bot')) { const next = new URLSearchParams(params); next.delete('bot'); setParams(next, { replace: true }); }
    if (connected) setNotice({ tone: 'good', text: 'Bot connected. Its content and testing stay in Auto Appy AI.' });
  };

  return (
    <StagePage className="wa-bots" label="Bots">
      <PageHeader
        eyebrow="Automate"
        title="Bots"
        description="Bots from Auto Appy AI answer your WhatsApp chats. Your team can take over any chat."
        actions={(
          <>
            <a className="ws-btn ws-btn--quiet" href={aiLibraryUrl(tenant)} target="_blank" rel="noreferrer"><Plus size={15} />Build a bot in AI<ArrowUpRight size={14} /></a>
            {canManage && <button type="button" className="ws-btn ws-btn--primary" onClick={() => setConnecting(true)} disabled={loading || !connection}><Link2 size={15} />Connect a bot</button>}
          </>
        )}
      />

      {!connection && <div className="wa-notice wa-notice--warn" role="alert"><CircleAlert size={16} /><span>Connect a WhatsApp number first. <Link to="/setup" style={{ fontWeight: 600, textDecoration: 'underline' }}>Connect WhatsApp</Link></span></div>}
      {error && <div className="wa-notice wa-notice--info" role="alert"><CircleAlert size={16} /><span className="ws-grow">{error}</span>{canManage && <button type="button" className="ws-btn ws-btn--sm ws-btn--quiet" onClick={reload}><RefreshCw size={14} />Retry</button>}</div>}
      {notice && <div className={`wa-notice wa-notice--${notice.tone}`} role={notice.tone === 'bad' ? 'alert' : 'status'}>{notice.tone === 'bad' ? <CircleAlert size={16} /> : <Check size={16} />}<span className="ws-grow">{notice.text}</span><button type="button" className="ws-btn ws-btn--plain" onClick={() => setNotice(null)}>Dismiss</button></div>}

      {canManage && connection && <StatusHero view={view} status={status} defaultBot={defaultBot} busy={busy} onToggle={toggle} loading={loading} />}

      {canManage && connection && (
        <section aria-label="Connected bots" className="wa-bots-section">
          <div className="ws-controls">
            <WsSegmented label="Bot kind" items={filters} value={kind} onChange={changeKind} />
            <div className="ws-controls-end">
              <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={reload} disabled={loading}><RefreshCw size={14} className={loading ? 'ws-spin' : ''} />Refresh</button>
            </div>
          </div>
          {loading && !view.bots.length && <div className="wa-bots-grid">{[0, 1, 2].map((i) => <Skeleton key={i} height={196} radius={20} />)}</div>}
          {!loading && view.error && <div className="wa-notice wa-notice--warn"><CircleAlert size={16} /><span>{view.error}</span></div>}
          {!loading && !view.error && shown.length === 0 && (
            <div className="ws-card wa-bots-empty">
              <AgentAvatar characterKey="nila" size={56} />
              <div className="ws-grow">
                <strong>{kind === 'guided' ? 'No guided chats connected' : view.data ? 'No bots in this list' : 'No bots connected yet'}</strong>
                <p className="ws-muted">{kind === 'guided' ? 'Build one in Auto Appy AI, publish it, then connect its bot ID.' : 'Build and test a bot in Auto Appy AI, then connect it here by its bot ID.'}</p>
              </div>
              <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={() => setConnecting(true)}><Link2 size={14} />Connect a bot</button>
            </div>
          )}
          {shown.length > 0 && (
            <div className="wa-bots-grid">
              {shown.map((bot) => <BotCard key={bot.id} bot={bot} view={view} tenant={tenant} isDefault={bot.id === view.channel?.default_bot_id} busy={busy} onDefault={makeDefault} onCopy={copy} />)}
            </div>
          )}
        </section>
      )}

      {kind === 'guided' && <GuidedExplainer tenant={tenant} />}

      <section className="ws-card wa-bots-how" aria-label="How bots work">
        {[
          { icon: Bot, title: 'Build and test in AI', text: 'Train a bot on your products and FAQs, or build a guided menu, then publish it.' },
          { icon: Link2, title: 'Connect it here', text: 'Paste its bot ID. Reuse the same bot on other channels.' },
          { icon: Hand, title: 'Take over any time', text: 'In Chats, your team can pause the bot for one person and reply themselves.' },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title}><span className="ws-icon-tile" aria-hidden="true"><Icon size={17} /></span><div><strong>{title}</strong><p className="ws-muted">{text}</p></div></div>
        ))}
        <Link to="/guide#bots" className="ws-btn ws-btn--plain"><BookOpen size={14} />Read the bots guide</Link>
      </section>

      {!!view.legacy_bots?.length && (
        <details className="ws-card wa-bots-legacy">
          <summary>Older WhatsApp automation ({view.legacy_bots.length})</summary>
          <p className="ws-muted">These records are kept. New bots are built in Auto Appy AI. Chat history stays in Chats; connecting AI doesn’t copy or delete them.</p>
          <ul>{view.legacy_bots.map((bot) => <li key={bot.id}>{bot.name} · {bot.is_active ? 'Previously on' : 'Off'}</li>)}</ul>
        </details>
      )}

      {connecting && <ConnectDialog view={view} tenant={tenant} initialBotId={params.get('bot') || ''} onSave={save} onClose={closeConnect} />}
    </StagePage>
  );
}
